import Globalize from 'globalize';
import type { LocaleId } from '../domain/types.js';
import {
  detectLocale,
  FALLBACK_LOCALE,
  getLocaleFromUrl,
  localeRegistry,
  LOCALE_STORAGE_KEY,
  resolveLocalizedImagePath,
  resolveSupportedLocale,
  withLocaleQuery
} from '../i18n/locales.js';

type MessageCatalog = Record<string, string>;

const fetchedCatalogs = new Map<LocaleId, MessageCatalog>();
const loadedLocales = new Set<string>();
let cldrLoaded = false;

const fetchJson = async <T>(path: string): Promise<T> => {
  const response = await fetch(path, { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}`);
  }
  return (await response.json()) as T;
};

const loadCldrData = async (): Promise<void> => {
  if (cldrLoaded) {
    return;
  }

  const [likelySubtags, plurals, ordinals, numberingSystems] = await Promise.all([
    fetchJson<object>('/resources/i18n/cldr/likelySubtags.json'),
    fetchJson<object>('/resources/i18n/cldr/plurals.json'),
    fetchJson<object>('/resources/i18n/cldr/ordinals.json'),
    fetchJson<object>('/resources/i18n/cldr/numberingSystems.json')
  ]);

  Globalize.load(likelySubtags, plurals, ordinals, numberingSystems);
  cldrLoaded = true;
};

const loadLocaleMainData = async (locale: LocaleId): Promise<void> => {
  const definition = localeRegistry[locale];
  const htmlTag = definition.htmlTag;
  if (loadedLocales.has(htmlTag)) {
    return;
  }

  const mainData = await fetchJson<object>(`/resources/i18n/cldr/main/${definition.cldrLocale}/ca-gregorian.json`);
  Globalize.load(mainData);

  loadedLocales.add(htmlTag);
};

const loadMessages = async (locale: LocaleId): Promise<MessageCatalog> => {
  const existing = fetchedCatalogs.get(locale);
  if (existing) {
    return existing;
  }

  const catalog = await fetchJson<MessageCatalog>(`/resources/i18n/${locale}.json`);
  fetchedCatalogs.set(locale, catalog);
  return catalog;
};

export const resolveMessage = (messages: MessageCatalog, fallbackMessages: MessageCatalog, key: string): string =>
  messages[key] ?? fallbackMessages[key] ?? key;

const applyDocumentLocale = (root: Document, locale: LocaleId): void => {
  const definition = localeRegistry[locale];
  root.documentElement.lang = definition.htmlTag;
  root.documentElement.dir = definition.dir;

  const localeMeta = root.head.querySelector('meta[property="og:locale"]');
  if (localeMeta) {
    localeMeta.setAttribute('content', definition.ogLocale);
  }
};

const applyMessages = (root: Document, locale: LocaleId, messages: MessageCatalog, fallbackMessages: MessageCatalog): void => {
  let globalize: Globalize | null = null;
  
  // Check if we have nodes that need number formatting
  const numberNodes = root.querySelectorAll<HTMLElement>('[data-i18n][data-i18n-format="number"]');
  if (numberNodes.length > 0) {
    try {
      globalize = new Globalize(localeRegistry[locale].htmlTag);
    } catch {
      // Number formatting not available, will skip number nodes
      globalize = null;
    }
  }

  const nodes = root.querySelectorAll<HTMLElement>('[data-i18n]');

  nodes.forEach((node) => {
    const key = node.dataset.i18n;
    if (!key) {
      return;
    }

    const value = resolveMessage(messages, fallbackMessages, key);
    const targetAttribute = node.dataset.i18nAttr;

    if (targetAttribute) {
      node.setAttribute(targetAttribute, value);
      return;
    }

    if (node.dataset.i18nFormat === 'number' && globalize) {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        try {
          node.textContent = globalize.formatNumber(numericValue);
          return;
        } catch {
          // Fall through to text content
        }
      }
    }

    node.textContent = value;
  });

  const title = resolveMessage(messages, fallbackMessages, 'seo.title');
  if (title !== 'seo.title') {
    root.title = title;
  }

  const description = root.head.querySelector('meta[name="description"]');
  const localizedDescription = resolveMessage(messages, fallbackMessages, 'seo.description');
  if (description && localizedDescription !== 'seo.description') {
    description.setAttribute('content', localizedDescription);
  }
};

const applyLocalizedImages = (root: Document, locale: LocaleId): void => {
  const nodes = root.querySelectorAll<HTMLImageElement>('[data-localized-image]');

  nodes.forEach((node) => {
    const fileName = node.dataset.localizedImage;
    if (!fileName) {
      return;
    }

    node.setAttribute('src', resolveLocalizedImagePath(locale, fileName));
  });
};

const readStoredLocale = (): string | null => {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const persistLocale = (locale: LocaleId): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Local storage can fail in private mode or restricted contexts.
  }
};

const updateSelectorValue = (root: Document, locale: LocaleId): void => {
  const selector = root.querySelector<HTMLSelectElement>('[data-locale-selector]');
  if (selector) {
    selector.value = locale;
  }
};

const updateUrlLocale = (locale: LocaleId): void => {
  const next = withLocaleQuery(new URL(window.location.href), locale);
  window.history.replaceState({}, '', next);
};

export const detectLocaleFromEnvironment = (): LocaleId => {
  const url = new URL(window.location.href);
  return detectLocale({
    storedLocale: readStoredLocale(),
    queryLocale: getLocaleFromUrl(url),
    navigatorLocales: navigator.languages,
    navigatorLocale: navigator.language
  });
};

export const localizeDocument = async (root: Document, locale: LocaleId): Promise<void> => {
  await loadCldrData();
  await loadLocaleMainData(locale);

  const [messages, fallbackMessages] = await Promise.all([loadMessages(locale), loadMessages(FALLBACK_LOCALE)]);
  Globalize.loadMessages({ [localeRegistry[locale].htmlTag]: messages, [localeRegistry[FALLBACK_LOCALE].htmlTag]: fallbackMessages });
  Globalize.locale(localeRegistry[locale].htmlTag);

  applyDocumentLocale(root, locale);
  applyMessages(root, locale, messages, fallbackMessages);
  applyLocalizedImages(root, locale);
  updateSelectorValue(root, locale);
};

export const bindLocaleSelector = (root: Document, onLocaleChanged: (locale: LocaleId) => void): void => {
  const selector = root.querySelector<HTMLSelectElement>('[data-locale-selector]');
  if (!selector) {
    return;
  }

  selector.addEventListener('change', () => {
    const resolved = resolveSupportedLocale(selector.value);
    if (!resolved) {
      selector.value = FALLBACK_LOCALE;
      return;
    }

    persistLocale(resolved);
    updateUrlLocale(resolved);
    onLocaleChanged(resolved);
  });
};
